import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { shouldHandleClickEventInternally } from './ScribeLinkUtils';
import createMarkup from 'utils/createMarkup';

/**
 * Creates script from a string, for example "<script>console.log('hello');</script>".
 * Thanks to this method, we don't need to use document.write. document.write is harmful
 * for performance and doesn't work when sandboxed on Internet Explorer / Edge.
 *
 * @param {String} scriptString A string with script's outerHTML, e.g. "<script>console.log('hello');</script>"
 * @param {DocumentElement=} doc Document element to attach script to. Defaults to current document.
 */
const createScriptFromString = (scriptString, doc = document) => {
  // Create temporary element so that we could easily extract innerHTML and src
  const el = doc.createElement('div');
  el.innerHTML = scriptString;
  const temporaryScriptTag = el.getElementsByTagName('script')[0];

  // Create final script element with necessary properties
  const scriptTag = doc.createElement('script');
  if (temporaryScriptTag.innerHTML) {
    scriptTag.innerHTML = temporaryScriptTag.innerHTML;
  } else if (temporaryScriptTag.getAttribute('src')) {
    scriptTag.setAttribute('src', temporaryScriptTag.getAttribute('src'));
  }
  doc.body.appendChild(scriptTag);
};

const loadScript = (src) => new Promise((resolve, reject) => {
  const attachListeners = tag => {
    tag.addEventListener('load', () => resolve(tag));
    tag.addEventListener('error', reject);
  };

  const existingScriptTag = document.getElementById('jQuery');

  if (existingScriptTag) {
    attachListeners(existingScriptTag);
  } else {
    const newScriptTag = document.createElement('script');
    newScriptTag.id = 'jQuery';
    attachListeners(newScriptTag);
    newScriptTag.src = src;
    document.body.appendChild(newScriptTag);
  }
});

const createSandbox = (scripts) => {
  const loadjQuery = () => (typeof window.jQuery === 'function'
    ? Promise.resolve()
    : loadScript('/static/vendor/jquery.min.js')
  );

  loadjQuery().then(() => {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('hidden', true);
    document.body.appendChild(iframe);

    // unfortunately, this gives the sandbox' scripts access to the parent's whole DOM node, but there's nothing
    // we can do to prevent it; hopefully Creative will not want to blow up the shop
    const sandboxWindow = iframe.contentWindow.window;
    const sandboxDocument = iframe.contentDocument || sandboxWindow.document;

    sandboxWindow.jQuery = window.jQuery.noConflict();

    scripts.forEach((scriptString => createScriptFromString(scriptString, sandboxDocument)));

    return iframe;
  });
};

/**
 * Renders the HTML content and automatically mounts (sandboxed, browser only) the scripts from the 'html' content
 * given. This is a general-purpose component, as FreeHtmls may be used in various places in Webshop/Raven.
 * If you need e.g. analytics, please use <FreeHtmlBanner> which wraps this component with additional logic.
 */
export default class FreeHtml extends Component {
  static propTypes = {
    html: PropTypes.string.isRequired,
    onHtmlLinkClick: PropTypes.func,
    updatable: PropTypes.bool,
  };

  static contextTypes = {
    router: PropTypes.object, // optional, available only when rendered inside the context
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    const nextHtml = nextProps.html;

    if (!nextHtml !== prevState.htmlProps) {
      // the FreeHtml content may include inline <style> and <script> tags; although the inline styles would be applied
      // if the content is dynamically added to DOM, this will not work for JavaScript, the <script> tags will be added
      // but the content will not get evaluated; we need to extract the scripts from the HTML/CSS and load it once
      // component get mounted in the browser DOM tree by React
      let htmlWithoutScripts = nextHtml;
      const scripts = [];

      const regExp = /<script\b[^>]*>([\s\S]*?)<\/script>/gmi;
      let match = regExp.exec(nextHtml);
      while (match) {
        // match[0] - full match, i.e. <script>...</script>
        // match[1] - actual script content
        htmlWithoutScripts = htmlWithoutScripts.replace(match[0], '');
        scripts.push(match[0]);

        match = regExp.exec(htmlWithoutScripts);
      }

      return {
        htmlProps: nextHtml,
        htmlWithoutScripts,
        scripts,
      };
    }

    return null;
  }

  state = {};

  componentDidMount() {
    this.handleExternalScripts();

    // capture the internal link clicks, as most routes could be handled internally
    const links = this.htmlContentElement.querySelectorAll('a');
    // without foreach check iPad/iOS9.3 throws 'undefined' error
    if (links && typeof links.forEach === 'function') {
      links.forEach(link => link.addEventListener('click', this.handleHtmlLinkClick));
    }
  }

  shouldComponentUpdate(nextProps) {
    if (!this.props.updatable) {
      return false;
    }

    return this.props.html !== nextProps.html;
  }

  componentWillUnmount() {
    if (this.sandbox) {
      this.sandbox.parentNode.removeChild(this.sandbox);
      this.sandbox = null;
    }
  }

  handleExternalScripts = () => {
    const { scripts } = this.state;

    if (!scripts || !scripts.length) {
      return;
    }

    this.sandbox = createSandbox(scripts);
  };

  /**
   * @param event {MouseEvent}
   */
  handleHtmlLinkClick = (event) => {
    if (this.props.onHtmlLinkClick) {
      this.props.onHtmlLinkClick(event);
    }

    if (!event.defaultPrevented && shouldHandleClickEventInternally(event)) {
      event.preventDefault();
      const anchorTag = event.currentTarget || event.target;
      this.context.router.push(`${anchorTag.pathname}${anchorTag.search}`);
    }
  };

  render() {
    // need to render without the inline <script> tags; otherwise, if rendered on the server side, scripts would
    // get executed twice; we also want to benefit from smaller html output from the server
    return (
      <div
        ref={(ref) => {
          this.htmlContentElement = ref;
        }}
        dangerouslySetInnerHTML={createMarkup(this.state.htmlWithoutScripts)}
      />
    );
  }
}
